import { type FC, type PropsWithChildren } from "react";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import clsx from "clsx";

import { useOutsideClick } from "@/lib/hooks/use-outside-click";
import { type ITelemetryCustom, sendTelemetryEvent } from "@/lib/telemetry";
import { useModalStackContext } from "@/src/contexts/ModelStackContext";
import { CloseIconSlimButtonClassName } from "@Components/Icons/CloseIconSlim";
import { requestTrack } from "@Components/Modules/Helpers";

import * as css from "./Modal.module.css";

export interface IModal extends PropsWithChildren {
    isOpen: boolean;
    onClose?: () => void;
    onKeyDown?: (_event: KeyboardEvent) => void;
    headerText?: string;
    hideCloseButton?: boolean;
    closeOnEsc?: boolean;
    closeOnBlur?: boolean;
    closeTrackingUrl?: string;
    closeCustomTelemetry?: ITelemetryCustom;
    trackingUrlCustom?: ITelemetryCustom;
    closeIconClickTelemetry?: ITelemetryCustom;
    allowBodyScroll?: boolean;
    clientId?: string;
    className?: string;
}

const Modal: FC<IModal> = (props) => {
    const [isOpen, setIsOpen] = useState(props.isOpen);
    const { onCloseModal, onOpenModal } = useModalStackContext();
    const [initialRender, setInitialRender] = useState(true);

    const {
        onClose: propsOnClose,
        closeTrackingUrl,
        closeCustomTelemetry,
        closeIconClickTelemetry,
        onKeyDown: propsOnKeyDown,
        closeOnEsc,
        closeOnBlur,
        allowBodyScroll,
        trackingUrlCustom,
    } = props;

    const onClose = useCallback(() => {
        setIsOpen(false);

        if (propsOnClose) {
            propsOnClose();
        }

        requestTrack(closeTrackingUrl);

        sendTelemetryEvent(closeCustomTelemetry);
    }, [propsOnClose, closeTrackingUrl, closeCustomTelemetry]);

    const modalRef = useOutsideClick<HTMLDivElement>(() => {
        if (isOpen === false || closeOnBlur === false) {
            return;
        }

        onClose();
    });

    const closeBtnClick = useCallback(() => {
        sendTelemetryEvent(closeIconClickTelemetry);

        onClose();
    }, [closeIconClickTelemetry, onClose]);

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (propsOnKeyDown) {
                propsOnKeyDown(e);
            }

            if (closeOnEsc && e.code === "Escape") {
                closeBtnClick();
            }
        },
        [propsOnKeyDown, closeOnEsc, closeBtnClick],
    );

    useEffect(() => {
        setIsOpen(props.isOpen);

        if (props.isOpen) {
            onOpenModal();
        } else {
            onCloseModal();
        }

        // Cleanup: only call onCloseModal on unmount if modal was open
        return () => {
            if (props.isOpen) {
                onCloseModal();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen]);

    useEffect(() => {
        if (isOpen) {
            window.addEventListener("keydown", onKeyDown);

            if (!allowBodyScroll) {
                document.body.style.overflow = "hidden";
            }

            sendTelemetryEvent(trackingUrlCustom);
        }

        setInitialRender(false);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = "";
        };
    }, [isOpen, onKeyDown, allowBodyScroll, trackingUrlCustom]);

    const closeButton = () => {
        if (props.hideCloseButton) {
            return null;
        }

        return (
            <CloseIconSlimButtonClassName
                className={css.closeButton}
                aria-label="Close"
                onClick={closeBtnClick}
                strokeWidth={10}
            />
        );
    };

    if (initialRender) {
        return false;
    }

    const modalContent = () =>
        props.isOpen && (
            <div id={props.clientId} className={clsx(css.modalComponent, props.className)}>
                <div className={css.wrapper} ref={modalRef}>
                    <div className={css.inner}>
                        {closeButton()}
                        {props.headerText && (
                            <h4
                                className={css.header}
                                dangerouslySetInnerHTML={{ __html: props.headerText }}
                            />
                        )}
                        <div className={css.content}>{props.children}</div>
                    </div>
                </div>
            </div>
        );

    const body = isOpen && modalContent();

    return createPortal(body, document.body);
};

export { Modal };
